import { z } from "zod";

// =============================================================================
// Call requests of /connect (error messages are keys of
// Connect.booking.form.errors) and the decisions of the back-office.
// =============================================================================

export const BOOKING_LIMITS = {
  name: 80,
  email: 254,
  company: 120,
  topic: 150,
  messageMin: 10,
  message: 3000,
} as const;

export const bookingRequestSchema = z.object({
  /** Start of the chosen slot (ISO instant) */
  startsAt: z.iso.datetime({ offset: true }),
  name: z
    .string()
    .trim()
    .min(1, { error: "required" })
    .max(BOOKING_LIMITS.name, { error: "tooLong" }),
  email: z
    .string()
    .trim()
    .min(1, { error: "required" })
    .max(BOOKING_LIMITS.email, { error: "tooLong" })
    .pipe(z.email({ error: "email" })),
  company: z
    .string()
    .trim()
    .max(BOOKING_LIMITS.company, { error: "tooLong" })
    .optional(),
  topic: z
    .string()
    .trim()
    .min(1, { error: "required" })
    .max(BOOKING_LIMITS.topic, { error: "tooLong" }),
  message: z
    .string()
    .trim()
    .min(1, { error: "required" })
    .min(BOOKING_LIMITS.messageMin, { error: "tooShort" })
    .max(BOOKING_LIMITS.message, { error: "tooLong" }),
  /** Time zone of the visitor's browser ("Europe/Paris") */
  timeZone: z.string().max(64),
  /** Honeypot: hidden from humans, bots fill it */
  website: z.string().optional(),
});

export type BookingRequestInput = z.input<typeof bookingRequestSchema>;
export type BookingRequest = z.output<typeof bookingRequestSchema>;

export const BOOKING_ERROR_KEYS = [
  "required",
  "email",
  "tooLong",
  "tooShort",
] as const;

export type BookingErrorKey = (typeof BOOKING_ERROR_KEYS)[number];

export function toBookingErrorKey(
  message: string | undefined
): BookingErrorKey {
  return BOOKING_ERROR_KEYS.find((key) => key === message) ?? "required";
}

/** Note sent with a decline or a cancellation (optional) */
export const bookingDecisionSchema = z.object({
  note: z
    .string()
    .trim()
    .max(1000, { error: "1000 characters max" })
    .optional(),
});

export type BookingDecision = z.infer<typeof bookingDecisionSchema>;

/** Rules of the calendar, edited in the settings */
export const bookingSettingsSchema = z
  .object({
    enabled: z.boolean(),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7),
    startHour: z.number().int().min(0).max(23),
    endHour: z.number().int().min(1).max(24),
    slotMinutes: z.union([
      z.literal(15),
      z.literal(30),
      z.literal(45),
      z.literal(60),
    ]),
    minNoticeDays: z.number().int().min(0).max(30),
    horizonDays: z.number().int().min(1).max(180),
    blockedDates: z.array(z.iso.date()).max(366),
    mirotalkBaseUrl: z.url({
      protocol: /^https$/,
      error: "Use an https:// URL",
    }),
  })
  .partial()
  .refine(
    (rules) =>
      rules.startHour === undefined ||
      rules.endHour === undefined ||
      rules.endHour > rules.startHour,
    { error: "The day must end after it starts", path: ["endHour"] }
  );

export type BookingSettingsInput = z.infer<typeof bookingSettingsSchema>;

// =============================================================================
